import { faChevronLeft } from '@fortawesome/free-solid-svg-icons'
import { Alert, AlertTitle, Stack } from '@mui/material'
import type { FC } from 'react'
import { useState, useContext } from 'react'
import styled from 'styled-components'

import { countries } from '@/common/countryData'
import Button from '@/components/Button'
import SlideInSidebar from '@/components/DEPRECATED_UI/SlideInSidebar'
import H5 from '@/components/H5'
import IconButton from '@/components/IconButton'
import SharecodeInput from '@/components/input/SharecodeInput'
import LoadingSpinner from '@/components/LoadingSpinner'
import P from '@/components/P'
import Select from '@/components/Select'
import TextInput from '@/components/TextInput'
import ctx from '@/context/AppContext'
import { useEditIdentityFieldsMutation } from '@/lib/apollo'
import type { Document, ReferenceTier, Sanctions } from '@/lib/apollo/query/get-applicant'
import { SanctionsStatus } from '@/lib/apollo/query/get-applicant'
import type { ErrorContext } from '@/lib/apollo/utils/getUpstreamErrors'
import { getErrorsFromGraphQLExtensions } from '@/lib/apollo/utils/getUpstreamErrors'
import vouchLogger from '@/lib/vouchLogger'
import type { IdentityVerification, IdentityProvider } from '@/types/Activity'
import { OverallOutcomeStatus } from '@/types/Outcome'
import { utcDateTimeStringToDateString } from '@/utils/date'
import { isTenant } from '@/utils/subjectHelpers'
import { isCorporateTier } from '@/utils/tier'

import CompanyNamedOfficerRadioGroup from './CompanyNamedOfficerRadioGroup'
import FacialMatchRadioGroup from './FacialMatchRadioGroup'
import IdEvidenceSummary from './IdEvidenceSummary'
import type { IdCheckSelectionEnum, R2RSelectionEnum } from './IDVTVerify'
import IDVTVerify from './IDVTVerify'
import LetterOfAuthorityRadioGroup from './LetterOfAuthorityRadioGroup'
import SanctionsVerification from './Sanctions'
import VerifyIdEvidenceFields from './VerifyIdEvidenceFields'
import type { Fields as VerifyIdEvidenceFieldsT } from './VerifyIdEvidenceFields'

const ConfirmButtons = styled.div`
  display: flex;
  justify-content: end;
`

const Container = styled.div`
  display: grid;
  gap: 15px;
`

const HeaderContainer = styled.div`
  display: grid;
  grid-template-columns: min-content 1fr;
  gap: 10px;
  align-items: center;
`

const EvidenceContainer = styled.div`
  margin-top: 20px;
  display: grid;
  gap: 15px;
`

export interface Props {
  customerId: string
  applicationId: string
  subjectId: string
  fullName: string
  firstName: string
  middleName?: string
  lastName: string
  nationality?: string
  dateOfBirth: string | null
  r2RShareCode?: string
  companyNamedOfficer: boolean | null
  letterOfAuthorityProvided: boolean | null
  facialMatch: boolean
  hasR2RChecks: boolean
  hasIdentityChecks: boolean
  identityVerification?: IdentityVerification
  documents: Document[]
  idEvidenceDocuments: Document[]
  idType: string
  subjectType: string
  referenceTier: ReferenceTier
  sanctions?: Sanctions
  onClose: () => void
  onSuccess: () => void
  overallOutcomeStatus?: OverallOutcomeStatus
  identityProvider?: IdentityProvider
}

interface Fields {
  firstName: string
  middleName: string
  lastName: string
  dateOfBirth: string | null
  r2RShareCode: string
  companyNamedOfficer: boolean | null
  letterOfAuthorityProvided: boolean | null
  id: VerifyIdEvidenceFieldsT
  facialMatch: boolean
  nationality: string
  sanctionsVerified: boolean | null
}

const VerifyIdSidebar: FC<Props> = ({
  customerId,
  applicationId,
  subjectId,
  fullName,
  firstName,
  middleName,
  lastName,
  nationality,
  dateOfBirth,
  hasR2RChecks,
  hasIdentityChecks,
  r2RShareCode,
  companyNamedOfficer,
  letterOfAuthorityProvided,
  facialMatch,
  documents,
  idEvidenceDocuments,
  idType,
  onClose,
  onSuccess,
  subjectType,
  referenceTier,
  identityVerification,
  sanctions,
  overallOutcomeStatus,
  identityProvider,
}) => {
  const { personId } = useContext(ctx)

  const [isEditingIdEvidence, setIsEditingIdEvidence] = useState<boolean>(false)
  const [hasEditedIdEvidence, setHasEditedIdEvidence] = useState<boolean>(false)
  const [hasUnexpectedError, setHasUnexpectedError] = useState<boolean>(false)
  const [errors, setErrors] = useState<ErrorContext<string>[] | undefined>()
  const [idCheckSelection, setIdCheckSelection] = useState<IdCheckSelectionEnum | ''>(
    identityVerification?.idCheckStatus ?? '',
  )
  const [r2rCheckSelection, setR2RCheckSelection] = useState<R2RSelectionEnum | ''>(
    identityVerification?.r2RStatus ?? '',
  )

  const initialEvidenceFileNames = idEvidenceDocuments.map(({ fileName }) => fileName)

  const isFinalOutcome = overallOutcomeStatus
    ? [
        OverallOutcomeStatus.accepted,
        OverallOutcomeStatus.pass,
        OverallOutcomeStatus.rejected,
      ].includes(overallOutcomeStatus)
    : false

  const isSanctionsVerificationRequired =
    sanctions !== undefined &&
    [
      SanctionsStatus.MATCH,
      SanctionsStatus.MISMATCH,
      SanctionsStatus.ACTION_REQUIRED,
    ].includes(sanctions.status)

  const hasVerifiedSanctions = isSanctionsVerificationRequired
    ? sanctions.status !== SanctionsStatus.ACTION_REQUIRED
    : null

  const [fields, setFields] = useState<Fields>({
    firstName,
    middleName: middleName ?? '',
    lastName,
    dateOfBirth: dateOfBirth ? utcDateTimeStringToDateString(dateOfBirth) : null,
    r2RShareCode: r2RShareCode ?? '',
    companyNamedOfficer,
    letterOfAuthorityProvided,
    facialMatch,
    id: {
      evidenceFileNames: initialEvidenceFileNames,
      type: idType,
    },
    nationality: nationality ?? 'GBR',
    sanctionsVerified: hasVerifiedSanctions,
  })

  const isCompanyNamedOfficerRequired = isCorporateTier(referenceTier)

  const isLetterOfAuthorityRequired =
    isCorporateTier(referenceTier) &&
    isTenant(subjectType) &&
    fields.companyNamedOfficer === false

  const [draftIdEvidenceValues, setDraftIdEvidenceValues] =
    useState<VerifyIdEvidenceFieldsT>({
      evidenceFileNames: initialEvidenceFileNames,
      type: '',
    })

  const [performEditIdentitySectionData, { loading }] = useEditIdentityFieldsMutation({
    onError: error => {
      vouchLogger.error('Verify ID mutation failed', {
        subjectId,
        hasIdentityChecks,
        idCheckSelection: idCheckSelection || 'Unset',
        errorName: error.name,
        errorKind: error.networkError ? 'network' : 'graphql',
      })
      setErrors(getErrorsFromGraphQLExtensions(error))
    },
  })

  const validateFields = (fields: Fields) => {
    let errors: ErrorContext<string>[] = []

    if (isSanctionsVerificationRequired && fields.sanctionsVerified !== true) {
      errors = [
        ...errors,
        {
          description:
            "Please confirm the applicant's sanctions status has been verified on Credas",
          reason: 'SanctionsVerificationRequired',
        },
      ]
    }

    if (isCompanyNamedOfficerRequired && fields.companyNamedOfficer === null) {
      errors = [
        ...errors,
        {
          description:
            'Please confirm if the applicant is a named officer of the company',
          reason: 'CompanyNamedOfficerRequired',
        },
      ]
    }

    if (isLetterOfAuthorityRequired && fields.letterOfAuthorityProvided === null) {
      errors = [
        ...errors,
        {
          description:
            'Please confirm if the applicant has a valid letter of authority to take out new tenancies in the company name',
          reason: 'LetterOfAuthorityRequired',
        },
      ]
    }

    if (hasIdentityChecks && idCheckSelection?.trim() === '') {
      errors = [
        ...errors,
        {
          description: 'Please select an ID check status',
          reason: 'IdCheckSelectionRequired',
        },
      ]
    }

    if (hasR2RChecks && r2rCheckSelection?.trim() === '') {
      errors = [
        ...errors,
        {
          description: 'Please select an R2R check status',
          reason: 'R2RCheckSelectionRequired',
        },
      ]
    }

    return errors
  }

  const handleSubmit = async () => {
    setHasUnexpectedError(false)
    setErrors(undefined)
    const errors = validateFields(fields)

    if (errors.length > 0) {
      vouchLogger.warning('Verify ID submit blocked by client validation', {
        subjectId,
        hasIdentityChecks,
        hasR2RChecks,
        idCheckSelection: idCheckSelection || 'Unset',
        validationReasons: errors.map(({ reason }) => reason),
      })
      setErrors(errors)
      return
    }

    if (fields.id.evidenceFileNames.length === 0) {
      vouchLogger.warning('Verify ID Submitted with no ID Evidence', {
        customerId,
        applicationId,
        subjectId,
      })
    }

    try {
      vouchLogger.info('Verify ID submit started', {
        subjectId,
        hasIdentityChecks,
        idCheckSelection: idCheckSelection || 'Unset',
        verificationPresentBefore: identityVerification !== undefined,
        previousIdCheckStatus: identityVerification?.idCheckStatus ?? 'Unset',
      })

      const result = await performEditIdentitySectionData({
        variables: {
          customerId,
          applicationId,
          subjectId,
          personId: personId ?? '',
          request: {
            firstName: fields.firstName,
            middleName: fields.middleName,
            lastName: fields.lastName,
            nationality: fields.nationality,
            dateOfBirth: fields.dateOfBirth ?? '',
            r2RShareCode: fields.r2RShareCode,
            companyNamedOfficer: fields.companyNamedOfficer,
            letterOfAuthorityProvided: fields.letterOfAuthorityProvided,
            facialMatch: fields.facialMatch,
            idCheckSelection: idCheckSelection !== '' ? idCheckSelection : undefined,
            r2rCheckSelection: r2rCheckSelection !== '' ? r2rCheckSelection : undefined,
            sanctionsVerified: fields.sanctionsVerified ?? undefined,
            ...(hasEditedIdEvidence
              ? {
                  id: fields.id,
                }
              : {}),
          },
        },
      })

      if (result.errors) {
        return
      }

      const status = result.data?.editIdentityFields.status

      if (status === 200) {
        vouchLogger.info('Verify ID submit succeeded', {
          subjectId,
          hasIdentityChecks,
          idCheckSelection: idCheckSelection || 'Unset',
          status,
        })
        onSuccess()
      } else {
        vouchLogger.warning('Verify ID submit returned non-success status', {
          subjectId,
          hasIdentityChecks,
          idCheckSelection: idCheckSelection || 'Unset',
          ...(status === undefined ? {} : { status }),
        })
      }
    } catch (err) {
      vouchLogger.error('Verify ID submit threw unexpectedly', {
        subjectId,
        hasIdentityChecks,
        idCheckSelection: idCheckSelection || 'Unset',
        errorName: err instanceof Error ? err.name : 'Unknown',
        errorKind: 'unexpected',
      })
      setHasUnexpectedError(true)
    }
  }

  const availableDocuments = documents.filter(
    document => document.type !== 'Individual Report',
  )

  const mappedIdEvidenceDocuments = fields.id.evidenceFileNames
    .map(evidenceFileName =>
      documents.find(({ fileName }) => fileName === evidenceFileName),
    )
    .filter(Boolean) as Document[]

  return (
    <SlideInSidebar
      onClose={onClose}
      header={
        isEditingIdEvidence ? (
          <HeaderContainer>
            <IconButton
              icon={faChevronLeft}
              tooltipMessage="Back"
              onClick={() => setIsEditingIdEvidence(false)}
            />
            <h1>All documents</h1>
          </HeaderContainer>
        ) : (
          <h1>Verify identity</h1>
        )
      }
      content={
        <Container>
          {loading ? (
            <LoadingSpinner size="2x" />
          ) : isEditingIdEvidence ? (
            <VerifyIdEvidenceFields
              documents={availableDocuments}
              draftValues={draftIdEvidenceValues}
              onChangeDraftValues={setDraftIdEvidenceValues}
              onCommitValues={newId => {
                setFields({
                  ...fields,
                  id: newId,
                })
                setIsEditingIdEvidence(false)
                setHasEditedIdEvidence(true)
              }}
              onClose={() => setIsEditingIdEvidence(false)}
            />
          ) : (
            <>
              <TextInput
                value={fields.firstName}
                label="First name"
                name="firstName"
                onChange={newFirstName =>
                  setFields({
                    ...fields,
                    firstName: newFirstName,
                  })
                }
              />
              <TextInput
                value={fields.middleName}
                label="Middle name"
                name="middleName"
                onChange={newMiddleName =>
                  setFields({
                    ...fields,
                    middleName: newMiddleName,
                  })
                }
              />
              <TextInput
                value={fields.lastName}
                label="Last name"
                name="lastName"
                onChange={newLastName =>
                  setFields({
                    ...fields,
                    lastName: newLastName,
                  })
                }
              />
              <Select
                label="Nationality"
                selectedValue={fields.nationality}
                options={countries.map(country => ({
                  value: country.alpha3,
                  label: country.nationality,
                }))}
                onSelect={e => setFields({ ...fields, nationality: e })}
              />
              <TextInput
                label="Date of birth"
                name="dateOfBirth"
                type="date"
                value={fields.dateOfBirth ?? ''}
                onChange={newDateOfBirth => {
                  setFields({
                    ...fields,
                    dateOfBirth: newDateOfBirth,
                  })
                }}
              />
              {isCompanyNamedOfficerRequired && (
                <CompanyNamedOfficerRadioGroup
                  applicantName={fullName}
                  value={fields.companyNamedOfficer}
                  onChange={(value: boolean) => {
                    setFields({
                      ...fields,
                      companyNamedOfficer: value,
                    })
                  }}
                />
              )}

              {isLetterOfAuthorityRequired && (
                <LetterOfAuthorityRadioGroup
                  applicantName={fullName}
                  value={fields.letterOfAuthorityProvided}
                  onChange={(value: boolean) => {
                    setFields({
                      ...fields,
                      letterOfAuthorityProvided: value,
                    })
                  }}
                />
              )}

              <FacialMatchRadioGroup
                facialMatch={fields.facialMatch}
                setFacialMatchSelection={value =>
                  setFields({
                    ...fields,
                    facialMatch: value,
                  })
                }
              />

              <EvidenceContainer>
                <H5>Evidence</H5>
                {isTenant(subjectType) && !isCorporateTier(referenceTier) && (
                  <SharecodeInput
                    onChange={newShareCode => {
                      setFields({
                        ...fields,
                        r2RShareCode: newShareCode,
                      })
                    }}
                    value={fields.r2RShareCode}
                  />
                )}
                <IdEvidenceSummary
                  documents={mappedIdEvidenceDocuments.map(document => ({
                    ...document,
                    type: fields.id.type,
                  }))}
                  onEdit={() => setIsEditingIdEvidence(true)}
                />
              </EvidenceContainer>
              <IDVTVerify
                hasR2RChecks={hasR2RChecks}
                hasIdentityChecks={hasIdentityChecks}
                idCheckSelection={idCheckSelection}
                setIdCheckSelection={setIdCheckSelection}
                r2rCheckSelection={r2rCheckSelection}
                setR2RCheckSelection={setR2RCheckSelection}
                identityProvider={identityProvider}
              />
              <SanctionsVerification
                isRequired={isSanctionsVerificationRequired}
                sanctionsVerified={fields.sanctionsVerified}
                setSanctionsVerified={(sanctionsVerified: boolean) =>
                  setFields({
                    ...fields,
                    sanctionsVerified,
                  })
                }
              />
              {isFinalOutcome && (
                <Alert severity="warning">
                  <AlertTitle>No changes will be made to this applicant.</AlertTitle>
                  <Stack spacing={2}>
                    <P>
                      This applicant has a final outcome and is only visible due to
                      Sanctions remediation required. Only the Sanctions checkbox is
                      operational. Any other changes you have made will not be updated.
                    </P>
                    <P>
                      Please ensure you have updated Credas appropriately before
                      submitting.
                    </P>
                  </Stack>
                </Alert>
              )}
              {(errors || hasUnexpectedError) && (
                <Alert severity="error">
                  <AlertTitle>
                    There was an error submitting the information provided.
                  </AlertTitle>
                  {errors !== undefined && errors.length > 0 ? (
                    <ol>
                      {errors.map(({ description, reason }, i) => (
                        <li key={i}>
                          {description} - ({reason})
                        </li>
                      ))}
                    </ol>
                  ) : (
                    <span>Please try again</span>
                  )}
                </Alert>
              )}
              <ConfirmButtons>
                <Button $alternate onClick={onClose}>
                  Cancel
                </Button>
                <Button
                  disabled={loading || fields.dateOfBirth === null}
                  onClick={handleSubmit}
                >
                  Update
                </Button>
              </ConfirmButtons>
            </>
          )}
        </Container>
      }
    />
  )
}

export default VerifyIdSidebar
